/**
 * Report Detail Page
 *
 * View and manage a single radiology report
 */

import { useState } from 'react'
import { useParams, useNavigate, Link } from 'react-router-dom'
import {
  ArrowLeft,
  FileText,
  Edit,
  CheckCircle,
  AlertCircle,
  Loader2,
  Clock,
  Calendar,
  Hash,
  Trash2,
  FilePlus,
  Image,
  Download,
  ShieldCheck,
  ShieldX,
} from 'lucide-react'

import {
  useReport,
  useSignReport,
  useUpdateReportStatus,
  useDeleteReport,
  formatReportDateTime,
  getReportStatusVariant,
  canEditReport,
  canSignReport,
  canAmendReport,
  canDeleteReport,
  downloadAndSaveReportPdf,
  verifyReportSignature,
  type VerificationResult,
} from '@/application/reports'
import { useStudy } from '@/application/studies'
import { usePatient } from '@/application/patients'
import { toast } from '@/lib/toast'
import { Button } from '@/presentation/components/ui/Button'
import { Badge } from '@/presentation/components/ui/Badge'
import { Card, CardContent, CardHeader, CardTitle } from '@/presentation/components/ui/Card'
import { Addenda } from '@/presentation/components/reports/Addenda'
import { REPORT_STATUS_LABELS, type ReportStatus } from '@/config/constants'
import { aFechaSegura } from '@/lib/utils'

// =============================================================================
// Component
// =============================================================================

export function ReportDetailPage() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()

  const [isDeleting, setIsDeleting] = useState(false)
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false)
  const [isDownloadingPdf, setIsDownloadingPdf] = useState(false)
  const [isVerifying, setIsVerifying] = useState(false)
  const [verificationResult, setVerificationResult] = useState<VerificationResult | null>(null)

  const { data: report, isLoading, isError, error } = useReport(id)
  const { data: study } = useStudy(report?.studyId)
  const { data: patient } = usePatient(study?.patientId)

  const signMutation = useSignReport()
  const statusMutation = useUpdateReportStatus()
  const deleteMutation = useDeleteReport()

  const handleSign = async () => {
    if (!id) return
    try {
      await signMutation.mutateAsync(id)
      toast.success('Informe firmado correctamente')
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Error al firmar el informe')
    }
  }

  const handleStatusChange = async (status: ReportStatus) => {
    if (!id) return
    try {
      await statusMutation.mutateAsync({ id, status })
      toast.success('Estado actualizado')
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Error al actualizar estado')
    }
  }

  const handleDelete = async () => {
    if (!id) return
    setIsDeleting(true)
    try {
      await deleteMutation.mutateAsync(id)
      toast.success('Informe eliminado')
      navigate('/reports')
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Error al eliminar informe')
      setIsDeleting(false)
    }
  }

  const handleDownloadPdf = async () => {
    if (!id || !patient) return
    setIsDownloadingPdf(true)
    try {
      const filename = `reporte_${patient.firstName}_${patient.lastName}_${new Date().toISOString().split('T')[0]}.pdf`
      await downloadAndSaveReportPdf(id, filename)
      toast.success('PDF descargado correctamente')
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Error al descargar el PDF')
    } finally {
      setIsDownloadingPdf(false)
    }
  }

  // Check if PDF can be downloaded (only FINAL or AMENDED)
  const canDownloadPdf = report?.status === 'FINAL' || report?.status === 'AMENDED'

  // Verify signature handler
  const handleVerifySignature = async () => {
    if (!id) return
    setIsVerifying(true)
    try {
      const result = await verifyReportSignature(id)
      setVerificationResult(result)
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Error al verificar la firma')
      setVerificationResult(null)
    } finally {
      setIsVerifying(false)
    }
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-[60vh]">
        <div className="text-center">
          <Loader2 className="h-8 w-8 animate-spin text-[var(--primary)] mx-auto mb-4" />
          <p className="text-[var(--foreground-muted)]">Cargando reporte...</p>
        </div>
      </div>
    )
  }

  if (isError || !report) {
    return (
      <div className="flex flex-col items-center justify-center h-[60vh] text-center">
        <AlertCircle className="h-12 w-12 text-red-500 mb-4" />
        <h2 className="text-xl font-semibold text-[var(--foreground)] mb-2">
          Reporte no encontrado
        </h2>
        <p className="text-[var(--foreground-muted)] mb-4">
          {error?.message || 'No se pudo cargar el reporte'}
        </p>
        <Button variant="outline" onClick={() => navigate(-1)}>
          <ArrowLeft className="h-4 w-4 mr-2" />
          Volver
        </Button>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-start justify-between">
        <div className="flex items-start gap-4">
          <Button variant="ghost" size="sm" onClick={() => navigate(-1)}>
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <div>
            <div className="flex items-center gap-3">
              <h1 className="text-2xl font-bold text-[var(--foreground)]">{report.title}</h1>
              <Badge variant={getReportStatusVariant(report.status)}>
                {REPORT_STATUS_LABELS[report.status]}
              </Badge>
            </div>
            <p className="text-[var(--foreground-muted)] mt-1">
              Versión {report.version} • Creado {formatReportDateTime(report.createdAt)}
            </p>
          </div>
        </div>

        {/* Actions */}
        <div className="flex items-center gap-2">
          {canDownloadPdf && (
            <Button
              variant="outline"
              size="sm"
              onClick={handleDownloadPdf}
              disabled={isDownloadingPdf}
            >
              {isDownloadingPdf ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <Download className="h-4 w-4 mr-2" />
              )}
              Descargar PDF
            </Button>
          )}
          {study && (
            <Link to={`/viewer/${study.id}`}>
              <Button variant="outline" size="sm">
                <Image className="h-4 w-4 mr-2" />
                Ver Imágenes
              </Button>
            </Link>
          )}
          {canEditReport(report.status) && (
            <Link to={`/reports/${report.id}/edit`}>
              <Button variant="outline" size="sm">
                <Edit className="h-4 w-4 mr-2" />
                Editar
              </Button>
            </Link>
          )}
          {canSignReport(report.status) && (
            <Button
              onClick={handleSign}
              disabled={signMutation.isPending}
            >
              {signMutation.isPending ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <CheckCircle className="h-4 w-4 mr-2" />
              )}
              Firmar
            </Button>
          )}
          {canAmendReport(report.status) && (
            <Link to={`/reports/${report.id}/amend`}>
              <Button>
                <FilePlus className="h-4 w-4 mr-2" />
                Enmendar
              </Button>
            </Link>
          )}
          {canDeleteReport(report.status) && (
            <Button
              variant="destructive"
              size="sm"
              onClick={() => setShowDeleteConfirm(true)}
            >
              <Trash2 className="h-4 w-4" />
            </Button>
          )}
        </div>
      </div>

      {/* Delete Confirmation */}
      {showDeleteConfirm && (
        <Card className="border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-950/40">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <AlertCircle className="h-5 w-5 text-red-500" />
                <span className="text-red-700 dark:text-red-300">
                  ¿Está seguro de eliminar este reporte? Esta acción no se puede deshacer.
                </span>
              </div>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setShowDeleteConfirm(false)}
                  disabled={isDeleting}
                >
                  Cancelar
                </Button>
                <Button
                  variant="destructive"
                  size="sm"
                  onClick={handleDelete}
                  disabled={isDeleting}
                >
                  {isDeleting ? (
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  ) : (
                    <Trash2 className="h-4 w-4 mr-2" />
                  )}
                  Eliminar
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main Content */}
        <div className="lg:col-span-2 space-y-6">
          {/* Findings */}
          <Card>
            <CardHeader>
              <CardTitle>Hallazgos</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="prose prose-sm max-w-none text-[var(--foreground)]">
                <pre className="whitespace-pre-wrap font-sans bg-transparent p-0 m-0">
                  {report.findings}
                </pre>
              </div>
            </CardContent>
          </Card>

          {/* Impression */}
          <Card>
            <CardHeader>
              <CardTitle>Impresión Diagnóstica</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="prose prose-sm max-w-none text-[var(--foreground)]">
                <pre className="whitespace-pre-wrap font-sans bg-transparent p-0 m-0">
                  {report.impression}
                </pre>
              </div>
            </CardContent>
          </Card>

          {/* Recommendations */}
          {report.recommendations && (
            <Card>
              <CardHeader>
                <CardTitle>Recomendaciones</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="prose prose-sm max-w-none text-[var(--foreground)]">
                  <pre className="whitespace-pre-wrap font-sans bg-transparent p-0 m-0">
                    {report.recommendations}
                  </pre>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Las notas agregadas despues de firmar. Debajo del informe y
              nunca mezcladas con los hallazgos. */}
          <Addenda
            reportId={report.id}
            sePuedeAgregar={report.status === 'FINAL' || report.status === 'AMENDED'}
          />

          {/* Amendment Info */}
          {report.amendmentReason && (
            <Card className="alert-warning border-0">
              <CardHeader>
                <CardTitle>Motivo de Enmienda</CardTitle>
              </CardHeader>
              <CardContent>
                <p>{report.amendmentReason}</p>
                {report.amendedFrom && (
                  <p className="text-sm opacity-80 mt-2">
                    Enmendado desde:{' '}
                    <Link
                      to={`/reports/${report.amendedFrom}`}
                      className="underline hover:no-underline"
                    >
                      ver reporte original
                    </Link>
                  </p>
                )}
              </CardContent>
            </Card>
          )}
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Report Info */}
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Información del Reporte</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center gap-3">
                <Hash className="h-4 w-4 text-[var(--foreground-muted)]" />
                <div>
                  <p className="text-xs text-[var(--foreground-muted)]">ID</p>
                  <p className="text-sm font-mono">{report.id.slice(0, 8)}...</p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <Calendar className="h-4 w-4 text-[var(--foreground-muted)]" />
                <div>
                  <p className="text-xs text-[var(--foreground-muted)]">Creado</p>
                  <p className="text-sm">{formatReportDateTime(report.createdAt)}</p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <Clock className="h-4 w-4 text-[var(--foreground-muted)]" />
                <div>
                  <p className="text-xs text-[var(--foreground-muted)]">Actualizado</p>
                  <p className="text-sm">{formatReportDateTime(report.updatedAt)}</p>
                </div>
              </div>
              {report.signedAt && (
                <div className="flex items-center gap-3">
                  <CheckCircle className="h-4 w-4 text-green-500" />
                  <div>
                    <p className="text-xs text-[var(--foreground-muted)]">Firmado</p>
                    <p className="text-sm">{formatReportDateTime(report.signedAt)}</p>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Signature Verification - Only for signed reports */}
          {canDownloadPdf && (
            <Card>
              <CardHeader>
                <CardTitle className="text-base">Verificación de Firma</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {!verificationResult ? (
                  <Button
                    variant="outline"
                    className="w-full"
                    onClick={handleVerifySignature}
                    disabled={isVerifying}
                  >
                    {isVerifying ? (
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    ) : (
                      <ShieldCheck className="h-4 w-4 mr-2" />
                    )}
                    Verificar Integridad
                  </Button>
                ) : (
                  <div className="space-y-3">
                    {/* Verification Result Badge */}
                    <div className={`flex items-center gap-2 p-3 rounded-lg ${
                      verificationResult.verification.isValid
                        ? 'bg-green-50 dark:bg-green-950/40 border border-green-200 dark:border-green-800'
                        : 'bg-red-50 dark:bg-red-950/40 border border-red-200 dark:border-red-800'
                    }`}>
                      {verificationResult.verification.isValid ? (
                        <ShieldCheck className="h-5 w-5 text-green-600 dark:text-green-400" />
                      ) : (
                        <ShieldX className="h-5 w-5 text-red-600 dark:text-red-400" />
                      )}
                      <div>
                        <p className={`font-medium ${
                          verificationResult.verification.isValid
                            ? 'text-green-700 dark:text-green-300'
                            : 'text-red-700 dark:text-red-300'
                        }`}>
                          {verificationResult.verification.isValid
                            ? 'Firma Válida'
                            : 'Firma Inválida'}
                        </p>
                        {verificationResult.verification.errorMessage && (
                          <p className="text-xs text-red-600 dark:text-red-400">
                            {verificationResult.verification.errorMessage}
                          </p>
                        )}
                      </div>
                    </div>

                    {/* Verification Details */}
                    {verificationResult.verification.isValid && (
                      <div className="text-xs text-[var(--foreground-muted)] space-y-1">
                        {verificationResult.verification.signerEmail && (
                          <p>Firmante: {verificationResult.verification.signerEmail}</p>
                        )}
                        {verificationResult.verification.algorithm && (
                          <p>Algoritmo: {verificationResult.verification.algorithm}</p>
                        )}
                        <p>Verificado: {new Date(verificationResult.verifiedAt).toLocaleString('es-CO')}</p>
                      </div>
                    )}

                    {/* Re-verify Button */}
                    <Button
                      variant="ghost"
                      size="sm"
                      className="w-full"
                      onClick={handleVerifySignature}
                      disabled={isVerifying}
                    >
                      {isVerifying ? (
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      ) : null}
                      Verificar de nuevo
                    </Button>
                  </div>
                )}
              </CardContent>
            </Card>
          )}

          {/* Patient Info */}
          {patient && (
            <Card>
              <CardHeader>
                <CardTitle className="text-base">Paciente</CardTitle>
              </CardHeader>
              <CardContent>
                <Link
                  to={`/patients/${patient.id}`}
                  className="flex items-center gap-3 hover:bg-[var(--background-subtle)] p-2 -m-2 rounded-lg transition-colors"
                >
                  <div className="h-10 w-10 rounded-full bg-[var(--primary)] flex items-center justify-center text-white font-medium">
                    {patient.firstName[0]}
                    {patient.lastName[0]}
                  </div>
                  <div>
                    <p className="font-medium text-[var(--foreground)]">
                      {patient.firstName} {patient.lastName}
                    </p>
                    <p className="text-xs text-[var(--foreground-muted)]">
                      {patient.document.type} {patient.document.number}
                    </p>
                  </div>
                </Link>
              </CardContent>
            </Card>
          )}

          {/* Study Info */}
          {study && (
            <Card>
              <CardHeader>
                <CardTitle className="text-base">Estudio</CardTitle>
              </CardHeader>
              <CardContent>
                <Link
                  to={`/studies/${study.id}`}
                  className="block hover:bg-[var(--background-subtle)] p-2 -m-2 rounded-lg transition-colors"
                >
                  <p className="font-medium text-[var(--foreground)]">
                    {study.studyDescription || 'Estudio sin descripción'}
                  </p>
                  <p className="text-sm text-[var(--foreground-muted)] mt-1">
                    {study.modality} • {aFechaSegura(study.studyDate).toLocaleDateString('es-CO')}
                  </p>
                  {study.accessionNumber && (
                    <p className="text-xs text-[var(--foreground-muted)] mt-1">
                      Acceso: {study.accessionNumber}
                    </p>
                  )}
                </Link>
              </CardContent>
            </Card>
          )}

          {/* Status Actions */}
          {report.status === 'DRAFT' && (
            <Card>
              <CardHeader>
                <CardTitle className="text-base">Cambiar Estado</CardTitle>
              </CardHeader>
              <CardContent className="space-y-2">
                <Button
                  variant="outline"
                  className="w-full justify-start"
                  onClick={() => handleStatusChange('PRELIMINARY')}
                  disabled={statusMutation.isPending}
                >
                  <Clock className="h-4 w-4 mr-2" />
                  Listo para Firmar
                </Button>
              </CardContent>
            </Card>
          )}

          {report.status === 'PRELIMINARY' && (
            <Card>
              <CardHeader>
                <CardTitle className="text-base">Cambiar Estado</CardTitle>
              </CardHeader>
              <CardContent className="space-y-2">
                <Button
                  variant="outline"
                  className="w-full justify-start"
                  onClick={() => handleStatusChange('DRAFT')}
                  disabled={statusMutation.isPending}
                >
                  <FileText className="h-4 w-4 mr-2" />
                  Volver a Borrador
                </Button>
              </CardContent>
            </Card>
          )}
        </div>
      </div>
    </div>
  )
}
